import { TABLE, TABLE_CELL, TABLE_HEAD, TableCard } from './TableCard';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useProductRuns } from './productRuns';
import { ExternalLink, Loader2, Plus } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useStableText } from '../../components/StableText';
import { useDragScroll } from './useDragScroll';
import { CatalogItemModal } from './CatalogItemModal';
import { AddAllToDb } from './AddAllToDb';
import { defaultFields, savePartToDb, useSaveAllToDb } from './partsToDb';
import { ImageLightbox } from './ImageLightbox';
import { TIP_LABEL, reasoningTooltip } from './ReasoningBadge';
import { localizeCategoryOrBlank, useCategories } from './catalogCategories';
import { invalidateCatalogItems, useCatalogItems } from './catalogItems';
import { formatPartName } from './formatNames';
import { hasBox } from './partCrop';
import { formatMm, formatPartSize, sizeKeyLabel } from './formatUnits';
import { ScrewTip } from './ScrewTip';
import { partCatalogId } from './partIdentity';
import { GeneratedCell, GenerateButton, ContextCell, type PartImageCol } from './PartImageCells';
import { PromptTooltip } from './PromptTooltip';
import {
  ALL_REREAD, isHardwareOrTool, setPartName, setPartSupplied, setPartSymmetry, SYMMETRIES,
  symmetryOf, usePartsRun,
  type PartItem, type Symmetry,
} from './partsRun';
import { wantsEstimate } from './dimensionsRun';
import { useEstimateRun } from './estimateRun';
import { usePartCategories } from './partCategories';
import { ListingCropCell, rowTone } from './ListingCropCell';
import { useHardwareCrops, useStepShots } from './hardwareCropsRun';
import { useAssemblySteps, type StepBand } from './categoryBands';
import {
  cropKey, cropRings, listingCropsPrompt, listingPictures, rawListingBoxes, toggleListingCrop,
  useSubmodelListingCrops, wantsListingCrop, type ListingCrop,
  type RawBox,
} from './listingCropsRun';
import { CallBadge } from './CallBadge';
import { SegmentedToggle } from './SegmentedToggle';
import { MasksBadge } from './MasksBadge';
import { rankIn, useListingSelection } from './listingSelectRun';
import { useSubmodels } from './listingStore';
import { useImageSizes } from './imageSize';
import { noMeasurements, scaleEstimates } from './sizeFromScale';
import { directionLabel, viewAxes } from './viewDirections';
import { useSubmodelFilter } from './submodelFilterStore';
import type { DrawnBox } from './BoxOverlay';
import { useProductColumn, type ProductCell } from './ProductColumn';

// A COLUMN THAT IS NOT A PART: every photograph with every part's crops drawn on it at once.
//
// The row shows one part per column against all eight pictures, which answers "where is this
// part" and cannot answer "what did the run make of this picture" — for that the eight
// answers have to be on one frame, and they were only ever on eight separate ones.
//
// A negative index because the columns ARE indices into the parts list, and there is no part
// here to give it a real one.
//
// AND THE WHOLE PRODUCT'S, in every row that has something to say about a product: its
// category and name, its submodel, the overall size the drawing prints. See ProductColumn,
// which is where those cells are read and why the other rows stay blank.
const ALL_PARTS = -1;

// One crop, with what the zoom needs to say about it and to write back through it: the part
// it belongs to, and its place among that part's crops ON THIS PHOTOGRAPH — which is what
// toggleListingCrop counts, and is NOT the position in a list that holds several parts.
type CropAt = { crop: ListingCrop; part: number; nth: number };

// A photograph's crops as the zoom draws them: every instance on it, which of them somebody
// has struck through, and whose it is. The ORDER is the contract — the zoom hands back the
// index of the box that was clicked, and that index is a position in this list.
// `frame` and `masks` are the zoom's two switches, and they are ALL the parts at once: the
// question they answer is "let me see the photograph", not "let me see this part" — which the
// tabs above the table already answer.
// ONE POLYGON OF ONE CROP, as the zoom's legend names it: a crop and a ring within it. Struck
// off by hand and only for as long as the picture is open, like the part switches beside them
// — a shape somebody wants off the picture to see what is under it is not a fact about the
// manual, and a run traces it again either way.
const ringKey = (crop: ListingCrop, ring: number) => `${cropKey(crop)}#${ring}`;

const drawnBoxes = (
  found: CropAt[], name: (part: number) => string, tone: (part: number) => string,
  frame = true, masks = true, hidden?: Set<number>,
  raw?: Record<number, RawBox[]>,
  ringsOff?: Set<string>,
  // The two numbers to write on this part's box, where the picture's direction says which
  // two they are — by part AND by which of that part's boxes this is. See viewAxes, and
  // `labelled` in the caller for why the second half matters.
  size?: (part: number, at: number) => { across?: string; up?: string },
  // How far from the camera this box is, in the reader's own language — see ListingCrop.depth.
  depth?: (d: 'near' | 'far' | 'spans') => string,
): DrawnBox[] =>
  found.map(({ crop, part, nth }, at) => ({
    // The stored rectangle, or the one the model sent, which is the same box before the
    // server padded it. A crop whose answer cannot be read back keeps the stored one — a
    // box that vanishes when a switch is flipped says the model found nothing.
    box: raw?.[part]?.[nth]?.box ?? crop.bbox,
    off: crop.off,
    // The shapes this part was traced as, less the ones struck off in the legend below the
    // picture. Filtered here and nowhere else: what a switch under the photograph means is
    // "take this off the photograph", and the crop in the store is untouched.
    outlines: masks
      ? cropRings(crop).filter((_, ring) => !ringsOff?.has(ringKey(crop, ring)))
      : undefined,
    // THE NAME, AND HOW FAR OFF IT IS. The depth rides in the label because that is where
    // somebody reading the picture asks the question — a box whose numbers look wrong is a
    // box you want to know the distance of, and the answer is two words away.
    label: crop.depth && depth ? `${name(part)} (${depth(crop.depth)})` : name(part),
    tone: tone(part),
    ...size?.(part, at),
    frame,
    // Marked rather than dropped: the list's ORDER is what the zoom hands back when a box
    // is clicked, and this one has to keep its place in it. See DrawnBox.
    hidden: hidden?.has(part),
  }));

// ONE KIND OF MARK ON THE ZOOMED PHOTOGRAPH, and whether it is drawn. The word names what
// the badge beside it reads back, and pressing it takes those marks off the picture — the
// same gesture, and the same struck-through look, as dismissing a single detection.
const MarkSwitch = ({ label, on, onToggle }: {
  label: string; on: boolean; onToggle: () => void;
}) => (
  <button
    type="button"
    onClick={onToggle}
    aria-pressed={on}
    className={`${TIP_LABEL} transition-colors hover:text-blue-500 ${
      on ? '' : 'line-through opacity-60'
    }`}
  >
    {label}
  </button>
);

// A cell whose answer is out being read again. Replaces the value rather than sitting
// beside it: the old value is what the call is going to overwrite, and leaving it on
// screen next to a spinner reads as a result that has already come in.
const Waiting = () => (
  <Loader2 className="mx-auto h-4 w-4 animate-spin text-slate-400" />
);

// THE parts table: one row per thing known about a part, one column per part shown.
//
// Every answer about a part, in one place. What the part IS (its category, its name,
// whether the database knows it, its description, its size), what this manual prints about
// it (a reference, a bag, a quantity, a letter), the drawing it is cut out of — and, given
// `imageCols`, the pictures a generation was given and the photographs it produced.
//
// The reading order is the working order, and the rule across the middle is the seam: TEXT
// above, PICTURES below. Above it, everything you can read about the part; below it, the
// manual's own drawing, then what was fed to the generator, then what came back — each one
// made from the one before it.
//
// Manuals differ in what they print — letters, references, bags, sizes, any or none of
// them — so the rows this part has nothing to say about are not drawn.
// One empty list, so a row with nothing to render does not restart the PDF pass every frame.
const NO_STEPS: StepBand[] = [];

export function PartsTable({
  items,
  crops,
  file,
  docId,
  sizing = false,
  coming,
  only,
  onlyCols,
  imageCols,
  imagesOnly = false,
  onZoomImage,
  onToggleContext,
  onRegenerate,
  onToggleDb,
}: {
  items: PartItem[];
  // Each part's drawing, cut out of the PDF, indexed like `items`. Rendered by the parent
  // rather than here: the generator is handed the very same picture, and two renders of
  // one crop would be two answers to "what does this part look like".
  crops: (string | null)[];
  // The manual itself, for the pages the hardware is found on — those are rendered where they
  // are drawn, being pictures nothing else in this app keeps. Absent, the hardware cells say
  // what they said before there was a call for them.
  file?: Blob;
  docId: string;
  // A size read is out right now. The extraction finishes first and repaints the table,
  // so without this the sizes on screen are the PREVIOUS run's, carried forward, sitting
  // there looking settled while the call that will replace them is still going.
  sizing?: boolean;
  // WHICH OF THE PHASE PRESS'S CALLS HAVE NOT HAPPENED YET. Both of these rows are showing
  // the LAST run's answer and both are about to be cleared and re-asked; each already waits
  // while its own call is out, and this is the window before it starts. Undefined when no
  // press is running, which is what tells a cell its answer is finished. See PartsPhase.
  coming?: { crops: boolean };
  // The one part to draw, by index into `items`. The whole list still arrives, and every
  // callback still speaks in its indices — a column is narrowed, nothing is renumbered.
  // Undefined draws every part.
  only?: number;
  // THE COLUMNS TO DRAW where `only` is not given, by part id — one kind's parts. Absent, all.
  onlyCols?: number[];
  // The generated half, one entry per part in the same order. Absent = the table is only
  // the list, which is what it is before anything has been generated.
  imageCols?: PartImageCol[];
  // ONLY THE PICTURE ROWS, for the phase that reconstructs a mesh out of them: the context
  // photographs and the render made from them, under the same header, without the twenty
  // rows of what the manual prints. See viewerPipelines.
  imagesOnly?: boolean;
  onZoomImage?: (url: string) => void;
  onToggleContext?: (url: string) => void;
  onRegenerate?: (index: number, currentImage?: string) => void;
  onToggleDb?: (
    index: number, historyIndex: number, image: string, added: boolean,
  ) => void | Promise<void>;
}) {
  const { t, language } = useLanguage();
  const st = useStableText();
  const cats = useCategories();
  const product = useProductColumn(docId, sizing);
  const { items: catalog, loaded: catalogLoaded } = useCatalogItems();
  // The part index whose "add to database" modal is open.
  const [addingIdx, setAddingIdx] = useState<number | null>(null);
  // WHICH GENERATED VERSION EACH PART HAS CHOSEN, by index into that part's history — null
  // for none chosen, which is a real answer: it is what makes the next press start a fresh
  // lineage rather than correct a picture. Undefined until the cell says, and it says on its
  // first render.
  //
  // Here rather than in the cell, where it was, because the button that acts on it is in the
  // row's LABEL now and the label is not inside any column. See PartImageCells.
  const [genAt, setGenAt] = useState<Record<number, number | null>>({});
  const genPick = (i: number) =>
    genAt[i] !== undefined
      ? genAt[i]
      : (imageCols?.[i]?.history.length ? imageCols[i].history.length - 1 : null);
  // The parts saved from this table since it was drawn, and the catalog id each became.
  // The extraction is what says a part is in the database, and it ran before the save
  // did — so without this, a part would go on offering its own button until the whole
  // phase was re-run. The id, because a part saved a moment ago links to its row like
  // any other: that is the moment you most want to look at what you just wrote.
  const [added, setAdded] = useState<Map<number, number>>(new Map());
  // The picture opened in the zoom overlay, the rectangle to draw on it when it has one,
  // and — for a listing photograph — WHICH PART it was opened from (null = closed).
  //
  // The drawing crops have no box: they ARE the cut-out. A listing photograph does, and it
  // also belongs to a set: the eight the run read. So the part travels with it, because
  // stepping to the next photograph has to bring that part's box on that photograph, and a
  // box is about a part and a picture together.
  // `shot` names the open picture among the SCHEMA row's, for the same reason `part` names
  // the listing set: two crops can be the same pixels — a part drawn twice in one step, a
  // page cut at the same box — so a data URL identifies nothing. See schemaShots.
  const [zoom, setZoom] = useState<{
    src: string; part?: number; shot?: string;
  } | null>(null);
  // WHAT THE ZOOM DRAWS ON THE PICTURE, from the two switches under it. Both on, which is
  // what the marks are for; either off, because the one thing a mark cannot show you is the
  // pixels underneath it — and "is that shape actually the rail" is answered by looking at
  // the rail. Not stored: it is a way of looking at THIS picture for a moment, and a
  // preference nobody set would be a photograph that opens bare a week later.
  const [showBoxes, setShowBoxes] = useState(true);
  const [showMasks, setShowMasks] = useState(true);
  // AND WHICH PARTS ARE OFF THE PICTURE ENTIRELY. The row above those two switches is one
  // name per part found here, and pressing a name takes its box and its shape together —
  // they are two marks about one claim, and "is the seat's outline right" is asked with the
  // other five parts out of the way. By part index, so it survives the arrows: the same
  // part struck through stays struck through on the next photograph.
  const [hiddenParts, setHiddenParts] = useState<Set<number>>(new Set());
  // AND WHICH SINGLE SHAPES ARE OFF IT. The same switch one level down: a part traced as four
  // polygons has four of them under its name, and taking one off is how you find out which
  // ring the stray one is — the answer a wash of one colour over four shapes cannot give.
  //
  // By crop and ring rather than by part, since one part is several crops on one picture. Kept
  // across the arrows for the same reason `hiddenParts` is: the key names the crop, so the
  // rings of a photograph you come back to are as you left them.
  const [hiddenRings, setHiddenRings] = useState<Set<string>>(new Set());
  // WHICH RECTANGLE THE ZOOM DRAWS: the one that is stored, or the one the model actually
  // sent. They differ by the padding the server adds on the way in (see rawListingBoxes),
  // and the gap between them is the only way to see how much of a loose-looking box is ours
  // and how much is the answer's.
  //
  // RAW BY DEFAULT, because that is the one being judged. A picture opened to ask "did it
  // find the part" is asking about the model's rectangle; the padded one is that rectangle
  // plus a margin of ours, and a loose-looking box that is loose because WE loosened it is
  // the answer read wrong.
  const [rawBoxes, setRawBoxes] = useState(true);
  // AND BACK TO RAW EVERY TIME ONE OPENS. The switch is a detour — you go and look at what
  // our padding added, and then you are looking at the padded box on the NEXT picture too,
  // with nothing on screen to say the last press was two photographs ago. Reset on the way
  // in, not on the way out, so stepping through the set with the arrows keeps whichever you
  // chose: the modal stays open, and that is one sitting.
  useEffect(() => {
    if (zoom) setRawBoxes(true);
    // Only as it opens: listing `zoom` itself would put it back on every step through the set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom != null]);
  // The part being re-read on its own, if one is. Every row above the seam is an answer
  // that call will replace, and until it lands the values on screen are the PREVIOUS
  // answer — settled-looking, and about to change. So they wait visibly, the way the size
  // row already did for the size read.
  //
  // Not the rows below it: a scoped re-read keeps the box (the schema has its own Redo)
  // and touches no picture, so those cells are not waiting for anything.
  const { rereading, reboxing } = usePartsRun(docId);
  // WHETHER THIS COLUMN IS WAITING ON THE EXTRACTION: the one part being re-read on its own,
  // or EVERY part while a whole-list run has not come back with a page yet — where what is on
  // screen is the previous run's list, kept only so the table holds its shape. See ALL_REREAD.
  const reread = (i: number) => rereading === ALL_REREAD || rereading === i;
  // Where each part was found in the listing photographs, and whether that call is out.
  const {
    byPart: listingCrops, busy: cropsBusy, asked: askedList,
    notes: cropNotes, read: cropsRead, toTrace: cropsToTrace,
    tracing: cropsTracing,
  } = useSubmodelListingCrops(docId);
  // Which parts a run has actually put the question to, so an empty cell can tell "found
  // nothing" from "not asked yet". A list in the store, a set here.
  const cropsAsked = useMemo(() => new Set(askedList), [askedList]);
  // AND WHERE THE HARDWARE WAS FOUND, which is the other half of this row: a listing photograph
  // shows an assembled product, so the parts it can answer for are the components, and the
  // screws are drawn in the manual instead. Same row, same cell, different pictures — see
  // hardwareCropsRun.
  const { byPart: pageCrops, asked: askedPages, busy: pageBusy } = useHardwareCrops(docId);
  const pageAsked = useMemo(() => new Set(askedPages), [askedPages]);
  // Which of those crops are kept per part — numbered on their boxes in the LISTING CROPS row.
  const selection = useListingSelection(docId);
  // THE SECOND PASS IS OUT, said by the only thing that knows: a total to trace.
  const masking = cropsToTrace > 0;
  // The photographs this row is about — the SELECTED submodel's, deduped by view. Read
  // once per render: it walks the listing store, and the row asks it three times.
  const submodel = useSubmodelFilter(docId);
  const listingPhotos = useMemo(() => listingPictures(docId), [docId, submodel]);
  // EVERY LISTING PHOTOGRAPH'S OWN PIXELS. A box is a fraction of a picture, and a fraction
  // says nothing about millimetres until it is a pixel count — so the size read off the
  // scale needs this for every picture, not only the open one. Cached by URL and measured
  // once (see useImageSizes).
  const pixels = useImageSizes(listingPhotos);
  // WHICH PHOTOGRAPHS ARE STILL OUT — the ones this run has not come back with. Undefined
  // when nothing is running, which is what tells a cell it is looking at a finished answer
  // rather than a run that has yet to reach these tiles.
  const cropsWaiting = useMemo(
    () => (cropsBusy ? new Set(listingPhotos.filter((u) => !cropsRead.includes(u))) : undefined),
    [cropsBusy, listingPhotos, cropsRead],
  );
  // AND EVERY PHOTOGRAPH, while the press that will re-read them all has not got here yet.
  // The same shape the row's own wait uses — a set of the pictures still outstanding — so a
  // cell needs to know nothing about which of the two is making it wait. First, because it is
  // the wider claim: nothing has been re-read, so nothing on screen is this run's.
  const comingCrops = useMemo(
    () => (coming?.crops ? new Set(listingPhotos) : undefined),
    [coming?.crops, listingPhotos],
  );
  // AND THE SAME ANSWER FOR THE TRACING, per cell rather than per row: this pass waits on
  // CROPS, and one photograph holds several — one part's shape can be back while the part
  // beside it is still out on the same picture. So a tile waits where a crop of ITS part on
  // that picture is still out, which is what the cell's `waiting` means.
  //
  // The shapes were cleared when the press started (see runListingMasks), so a tile that is
  // waiting is showing its boxes and no mask — which is the true state and reads as one.
  const tracingKeys = useMemo(() => new Set(cropsTracing), [cropsTracing]);
  const masksWaiting = useMemo(
    () => (cs: ListingCrop[]) => (masking
      ? new Set(cs.filter((c) => tracingKeys.has(cropKey(c))).map((c) => c.url))
      : undefined),
    [masking, tracingKeys],
  );
  // Which part the estimate press is on, which it still owes, and which failed — see
  // estimateRun, whose press is beside the phase button.
  const estimates = useEstimateRun(docId);
  // Which part's name is being typed, and what has been typed so far — one at a time,
  // because it is one text field opened where the old name was.
  const [renaming, setRenaming] = useState<{ at: number; draft: string } | null>(null);
  const escaped = useRef(false);
  const scrollRef = useDragScroll<HTMLDivElement>();
  // Where "back" goes, carried in the link rather than left to browser history: the
  // catalog is a page you ARRIVE at, and it should say where from. History would do
  // until the link is opened in a new tab, where there is nothing behind it.
  const here = useLocation();
  const from = encodeURIComponent(`${here.pathname}${here.search}`);
  // The catalog row this part is, or null — asked of the catalog as it is NOW.
  //
  // Everything that CLAIMS a row names it from somewhere else: the model matched this
  // part while extracting (it saw the drawing, and knows a barrel nut whatever the
  // manual called it, but it answered once and will not answer again unpaid), and
  // `added` remembers this session's saves. Both are ids, and an id is a claim about a
  // row — not the row. Delete that row and the claim reads exactly as it did before.
  //
  // So a claim is only worth anything if it still resolves, and what it resolves
  // against is the catalog as it is now. When nothing claims this part, or the claim is
  // gone, its name is asked instead — which is also what survives a reload, when
  // nothing is left claiming anything.
  //
  // None of this can happen before the catalog is known. Until then the claim stands as
  // it is: a list nobody has read yet is not evidence that a row is missing, and
  // treating it as one would blink every YES to NO on load and offer to save what is
  // already saved.
  const known = (p: PartItem, i: number) =>
    partCatalogId(catalog, catalogLoaded, p, p.catalogId ?? added.get(i) ?? null);
  // EVERY PART THE DATABASE DOES NOT HAVE, for the button that saves them all at once —
  // over the whole list, whichever part is open. Not before the catalog is read: a list
  // nobody has fetched is not evidence that a row is missing.
  const saveAll = useSaveAllToDb(docId, (at, id) => setAdded((m) => new Map(m).set(at, id)));
  const missing = catalogLoaded
    ? items.flatMap((item, at) => (known(item, at) == null ? [{ at, item, crop: crops[at] ?? null }] : []))
    : [];

  // THE DATABASE'S CATEGORY, falling back to the model's — see usePartCategories. The whole
  // list is posted as the ruler, so this is indexed like the list rather than like the question.
  const allCategories = usePartCategories(items, added);
  const partCategory = (_p: PartItem, i: number) => allCategories[i] ?? '';

  // The columns actually drawn, as indices into `items`. Everything below maps over THIS
  // and looks the part up — so a callback still receives the part's own index and a
  // narrowed table is not a renumbered one. Which is also what lets them be REORDERED:
  // an index is the part, and the order they are read in is a separate question.
  //
  // IN ID ORDER — the selector's order, because the selector and this table are the same
  // list read two ways and a tab strip that goes seat, rail, screw over a table that goes
  // screw, seat, rail makes you find the part twice.
  //
  // It was components, then hardware, then tools, in both places, and it moved for the
  // reason the strip's did: every segment now carries its id, so the number is what a
  // reader arrives with. This is the same argument as before with the two ends still tied
  // together — what changed is which order they are tied to.
  //
  // Nothing to sort. A supplied part's id is its position in `items`, so counting up the
  // indices IS counting up the ids.
  const partCols = only == null && onlyCols ? onlyCols : only == null
    ? items.map((_, i) => i)
    : items[only] ? [only] : [];

  // Which of those columns the listing row is even about: the components, which ARE the
  // object being photographed, and the hardware whose job is to be seen. See
  // wantsListingCrop — the tools were put away before the picture was taken and the rest of
  // the hardware is inside the joints.
  //
  // The row is dropped when none of the parts on screen qualifies, rather than drawn with a
  // button that would do nothing. On a screwdriver's own tab that button's answer is known
  // before it is pressed.
  // Which part the open zoom belongs to, pulled out so the navigate callback can close over
  // a plain number rather than over `zoom`, which is null the moment it matters.
  const zoomPart = zoom?.part;
  // WHAT THE MODEL SAID ABOUT THE PHOTOGRAPH ON SCREEN, to show under it. The answers in
  // this row are rectangles, and a rectangle is a thing you can only accept or dismiss; the
  // sentence behind it is the half you can argue with — and the only way to read a picture
  // that came back empty, where "the buttons are in shadow here" and a model that never
  // understood the question look identical from outside.
  //
  // Only for a listing photograph, and only once a run has been through that picture: a
  // part's drawing has no call behind it to report.
  const zoomNote = zoomPart == null ? undefined : cropNotes[zoom?.src ?? ''];
  // The unpadded boxes, read out of that same answer — only while the switch asks for them,
  // since it is a JSON parse of up to a few hundred lines and nobody is owed it by default.
  const zoomRaw = useMemo(
    () => (rawBoxes && zoomNote ? rawListingBoxes(zoomNote.raw, items) : undefined),
    [rawBoxes, zoomNote, items],
  );
  // WHAT THIS SUBMODEL IS MADE OF, and which of those each part is. The options are Phase
  // 2's own reading of the selected submodel's listing, lined up by majority vote — one
  // list, so the model's pick and a hand-picked one can never come from different sets.
  // Every drawing of each part the steps hold, and those boxes cut out of the PDF.
  // WHAT THAT SEARCH NEEDS BEFORE IT CAN BE ASKED: the steps, and the repartition saying
  // which parts each step adds. It looks in one step at a time, for that step's own parts —
  // so with no repartition there is nothing to look for, and the run has always answered
  // that correctly by doing nothing at all. Which reads exactly like a broken button.
  //
  // It bites where the same manual carries more than one run: a repartition done in an
  // earlier one is not this run's, and the parts table looks complete either way —
  // nothing above this row needs the steps.
  // EVERY DRAWING THE SCHEMA ROW HOLDS, in the order it is shown: each part's portrait, part
  // after part across the row.
  //
  // One set, not one per part, because the question the arrows answer is "what does the next
  // one look like" and the row is a row — a reader comparing the wrench with the tool beside
  // it should not have to close the picture, find the neighbouring column and open that.
  //
  // It used to carry each part's STEP crops after its portrait. Those are the listing-crops
  // row's manual half now (see hardwareCropsRun).
  //
  // Identified by column rather than by URL: two crops of the same box are the same string,
  // and a set that steps by pixels would jump to the wrong one.
  const schemaShots = useMemo(() => {
    const out: { id: string; src: string }[] = [];
    for (const i of partCols) {
      if (hasBox(items[i]?.bbox) && crops[i]) out.push({ id: `p${i}`, src: crops[i]! });
    }
    return out;
  }, [partCols, items, crops]);
  const shotIds = useMemo(() => schemaShots.map((s) => s.id), [schemaShots]);

  const submodels = useSubmodels(docId);
  // WHICH SIDE EACH PHOTOGRAPH SHOWS, by URL — Phase 1's answer, said in the zoom beside the
  // counter. Across every submodel: the row draws whichever pictures the filter left, and a
  // direction is a fact about the picture rather than about the colour it is a picture of.
  //
  // INCLUDING "Other", which is an answer: somebody looked at this picture and could name no
  // side for it — an in-situ shot, a detail crop, two chairs at a table. What stays blank is
  // a picture nothing has classified at all, and those two are different facts.
  const viewOfPhoto = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of submodels) {
      s.gallery.forEach((url, i) => {
        const v = s.galleryViews[i];
        if (v && !m.has(url)) m.set(url, v);
      });
    }
    return m;
  }, [submodels]);
  // AND WHICH TWO DIMENSIONS THIS PICTURE SHOWS, which is that same answer read for a
  // different purpose: a front view shows the width across and the height up, a side view
  // the depth across. Null on a 45° shot and on one nobody could name — see viewAxes.
  const zoomAxes = zoom && zoomPart != null ? viewAxes(viewOfPhoto.get(zoom.src)) : null;
  // The numbers to write on one part's box: the two the picture shows, where they are known.
  // Nothing where they are not — half a size draws its half, and a part nothing has measured
  // draws a plain rectangle, as it always did.
  // Whether the whole product has a model in the database for this colour — see the
  // "found in database" row.
  // NULL until the server has answered: a spinner in the cell, not a NON (see useProductRuns).
  const productInDb = useProductRuns(docId, submodel).runs?.some((j) => j.inCatalog) ?? null;

  const asksListing = (i: number) => !!items[i] && wantsListingCrop(items[i], allCategories[i]);
  const listingCols = partCols.filter(asksListing);
  // The columns the MANUAL call answers for: every part. A component is looked for in the
  // photographs AND in the steps; hardware and tools, which no photograph of the assembled
  // product shows, in the steps alone. See hardwareCropsRun.
  const asksPage = (i: number) => !!items[i];
  const pageCols = partCols.filter(asksPage);
  // THE PICTURES THE HARDWARE WAS LOOKED FOR IN: every assembly step's own bands, in printed
  // order — EXACTLY the pictures the calls carry.
  //
  // Not the pages they were cut from, which is what this drew first. A page with two steps on
  // it shown under one step's answer is a picture nobody was asked about: the box is right and
  // the frame around it is somebody else's question, and on a page holding three steps the
  // same tile appeared three times over. The band is the question's own frame.
  //
  // And only once a run has happened. Rasterising every step of a manual nobody has pressed
  // this on is a pass over the PDF for a row that would draw nothing.
  const { steps: assemblySteps } = useAssemblySteps(docId);
  const bandsFor = useMemo(
    () => (pageCols.length && (askedPages.length || pageBusy) ? assemblySteps : NO_STEPS),
    [pageCols.length, askedPages.length, pageBusy, assemblySteps],
  );
  const stepPictures = useStepShots(file ?? null, bandsFor);
  // FLAT, in step order, which is the spine every cell of this half hangs its boxes off — the
  // same picture in the same place in every column, as the photograph half does with the eight
  // listing shots.
  const pagePhotos = useMemo(
    () => bandsFor.flatMap((one) => (stepPictures.get(one.category) ?? []).map((b) => b.src)),
    [bandsFor, stepPictures],
  );
  // ONE PART'S STEP CROPS AS THE CELL TAKES THEM: a picture and a rectangle, which is what a
  // listing crop is. The step and the band index become a rendered URL here and nowhere else —
  // the store keeps names, because a data URL identifies nothing and would be a megabyte on
  // the server.
  const pageCropsOf = (i: number): ListingCrop[] =>
    (pageCrops[i] ?? []).flatMap((c) => {
      const url = stepPictures.get(c.step)?.[c.shot]?.src;
      return url ? [{ url, bbox: c.bbox, ...(c.rawBbox ? { rawBbox: c.rawBbox } : {}) }] : [];
    });
  // WHETHER THE OPEN PICTURE IS A PAGE OF THE MANUAL rather than a listing photograph, which
  // is the only thing the zoom needs to know to draw the right half of this row's answers on
  // it. Asked of the URL rather than carried in the zoom's state: the two sets of pictures are
  // disjoint — one is the listing's files, the other is rendered from the PDF — so the picture
  // already says which call it belongs to, and a flag beside it could only disagree.
  const pageUrls = useMemo(() => new Set(pagePhotos), [pagePhotos]);
  const zoomOnPage = !!zoom && pageUrls.has(zoom.src);
  // Whether this row is drawn at all — either half is enough.
  const drawsListing = listingPhotos.length > 0 && listingCols.length > 0;
  // AND THE MANUAL'S ROW, on its own: drawn once it has pages to show —
  const drawsPages = (pagePhotos.length > 0 && pageCols.length > 0)
    // AND WHILE THE PAGE CALL IS OUT, before it has found the first page. Otherwise a manual
    // with no listing photographs shows no row at all for the length of the run and then the
    // finished answer, which is the one report a running press must not make.
    || (pageBusy && pageCols.length > 0);
  // FIRST, and only under "All parts" — on one part's own tab the tab is that part, and the
  // product beside it would be a column about something else. It no longer waits for the
  // listing row: the product's size and name are worth a column on a manual with no
  // photographs.
  const cols = only == null && partCols.length ? [ALL_PARTS, ...partCols] : partCols;
  // One part on screen: the table's rows are all about it, so a row's label can hold that
  // row's control. With several, a label speaks for no one column in particular.
  const onePart = partCols.length === 1;

  // What to call a part where its name has to be written on a picture. The same name the
  // column heading uses, so a box in the zoom and the column it came from agree.
  const partAlt = (i: number) =>
    items[i]?.nameEn || items[i]?.name || t('notice.parts.piece');

  // AND WHAT COLOUR IT IS, everywhere it is drawn — its place among the parts being looked
  // for, which is the same on all eight photographs and in its own column. See partTone.
  // ONE COLOUR PER PART, on a photograph and on a page alike — see rowTone.
  const toneOfPart = (i: number) => rowTone(i, listingCols, partCols);
  const toneOfCrop = (part: number) => () => toneOfPart(part);

  // EVERY CROP ON ONE PHOTOGRAPH, from the parts asked for — each with the part it belongs to
  // and its place in that part's own list. Read from the store on every render rather than
  // captured when the zoom opened: striking a box through writes to that store, and a zoom
  // holding its own copy would go on showing the box the way it was before you clicked it.
  const cropsOn = (url: string, parts: number[]): CropAt[] =>
    parts.flatMap((part) => (listingCrops[part] ?? [])
      .filter((c) => c.url === url)
      .map((crop, nth) => ({ crop, part, nth })));

  // AND THE SAME ON A PAGE OF THE MANUAL, from the other call. A page crop is a picture and a
  // rectangle like any other, so everything downstream — the boxes, the legend, the raw
  // outline — takes it unchanged; what differs is the store it is read from.
  const pageCropsOn = (url: string, parts: number[]): CropAt[] =>
    parts.flatMap((part) => pageCropsOf(part)
      .filter((c) => c.url === url)
      .map((crop, nth) => ({ crop, part, nth })));

  // The boxes on the picture being zoomed. One part's, or — from the ALL PARTS column —
  // every part's, which is the only view in which one run's answers can be read against each
  // other rather than one at a time.
  const zoomCrops = zoom == null || zoomPart == null
    ? []
    : zoomOnPage
    // A MANUAL PAGE OPENS WITH ITS BOXES ON IT TOO. It used to open bare: the cell handed the
    // zoom a URL and nothing else, so the part never travelled with the picture and there was
    // nothing to draw — a page at full size, with the one thing the tile had shown missing.
    ? pageCropsOn(zoom.src, zoomPart === ALL_PARTS ? pageCols : [zoomPart])
    : cropsOn(zoom.src, zoomPart === ALL_PARTS ? listingCols : [zoomPart]);

  // THE PICTURES THE ARROWS WALK: the ones the cell it was opened from drew, and no others.
  // Which is the whole set for the column — a hardware part's pages, a component's listing
  // photographs, and under ALL PARTS both, in the order the tiles are in.
  //
  // Said from the COLUMN, so a cell holding both kinds steps from the last photograph into the
  // first page instead of stopping at the seam.
  const zoomGallery = useMemo(() => {
    if (zoomPart == null) return listingPhotos;
    if (zoomPart === ALL_PARTS || asksListing(zoomPart)) return [...listingPhotos, ...pagePhotos];
    return pagePhotos;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomPart, listingPhotos, pagePhotos, allCategories, items]);

  // ONE LABELLED BOX PER PART, and it is the biggest one. A part found twice on a
  // photograph is two rectangles of different sizes — the near leg and the far one — and the
  // measurement describes the part ONCE: written on both, it claims two scales for one
  // picture, and the smaller box is the one the number fits worst. The largest is the
  // nearest to the camera and the least foreshortened, so it is the one that keeps it.
  //
  // By position in `zoomCrops`, which is the list drawnBoxes walks: same order, same index.
  const labelled = useMemo(() => {
    const at = new Map<number, number>();
    const area = new Map<number, number>();
    zoomCrops.forEach(({ crop, part }, i) => {
      const [x0, y0, x1, y1] = crop.bbox;
      const size = Math.max(0, x1 - x0) * Math.max(0, y1 - y0);
      if (size > (area.get(part) ?? -1)) {
        area.set(part, size);
        at.set(part, i);
      }
    });
    return at;
  }, [zoomCrops]);
  // HOW MANY MILLIMETRES A PIXEL OF THIS PHOTOGRAPH IS WORTH, per part and per axis — the
  // same numbers the box edges carry, divided by the pixels they span.
  //
  // Everything needed was already on the page: the size, the box, and the picture. The one
  // thing fractions cannot give is the pixel count, which is what useImageSizes is for — and
  // it is also the trap, since one unit of x is the whole WIDTH and one of y the whole
  // HEIGHT, so the two axes sit an aspect ratio apart until both are turned into pixels.
  //
  // It is a reading, not a rule: nothing is filtered by it. Two parts on one photograph
  // should agree to within the perspective between them, and where they do not, one of the
  // numbers or one of the boxes is wrong.
  const shotPixels = pixels.get(zoom?.src ?? '');
  const perPixel = (part: number, at: number) => {
    const found = zoomCrops[at];
    if (!zoomAxes || !shotPixels || !found) return null;
    // NOT FOR A BOX BEHIND THE REST OF IT. A part further from the camera spans fewer pixels
    // for the same millimetres, so its scale reads high — comparing it with a near part's is
    // comparing two distances and calling the difference an error.
    //
    // A PART THAT SPANS FRONT TO BACK IS FINE, though, and it is the best row in the table: a
    // seat or a side rail reaches from the near face of the object to the far one, so what it
    // averages is the middle — which is the distance the picture as a whole is at. Only `far`
    // is a distance of its own. Unknown is read as near, which is what every crop found
    // before the question was asked has to be.
    if (found.crop.depth === 'far') return null;
    const size = items[part]?.size;
    const [x0, y0, x1, y1] = zoomRaw?.[part]?.[found.nth]?.box ?? found.crop.bbox;
    const across = size?.[zoomAxes.across];
    const up = size?.[zoomAxes.up];
    const wide = Math.max(0, x1 - x0) * shotPixels.w;
    const tall = Math.max(0, y1 - y0) * shotPixels.h;
    return {
      across: typeof across === 'number' && wide > 1 ? across / wide : null,
      up: typeof up === 'number' && tall > 1 ? up / tall : null,
    };
  };
  // ONE ROW PER PART on the open photograph, off the same box its measurement is written on
  // — so the table and the picture cannot disagree.
  //
  // EVERY PART, including the ones with nothing on either axis. An empty row is the answer
  // to "why is there no number on that box": nobody has measured this part. Dropped, the
  // table said the same thing by being shorter than the legend above it, which is a fact
  // about a list somebody has to notice the absence of.
  const scales = [...labelled.entries()]
    .map(([part, at]) => ({ part, ...(perPixel(part, at) ?? { across: null, up: null }) }));
  // AND THE PICTURE'S OWN SCALE, one number per axis: the mean of what its parts imply.
  //
  // A mean and not a pick, because every row is a reading of the same photograph and none of
  // them is the authority — two parts at the same distance should agree, and where they
  // differ by a percent or two it is the boxes being a few pixels loose. What is already out
  // is the only thing that would bias it: a part BEHIND the rest of the object never gets a
  // row (see perPixel), so nothing far is being averaged in with something near.
  const meanScale = (pick: 'across' | 'up'): number | null => {
    const got = scales.map((r) => r[pick]).filter((v): v is number => v != null);
    return got.length ? got.reduce((a, b) => a + b, 0) / got.length : null;
  };
  const boxSize = (part: number, at: number) => {
    if (!zoomAxes || labelled.get(part) !== at) return {};
    const size = items[part]?.size;
    const across = size?.[zoomAxes.across];
    const up = size?.[zoomAxes.up];
    // NAMED, not just numbered. "415 mm" on an edge is a length whose axis you work out
    // from which edge it is on; "width: 415 mm" is the answer itself, and the word is the
    // one the size cell already uses for that key.
    const said = (key: 'x' | 'y' | 'z', mm: unknown) => (typeof mm === 'number'
      ? `${sizeKeyLabel(key)}: ${formatMm(mm)}`
      : undefined);
    return { across: said(zoomAxes.across, across), up: said(zoomAxes.up, up) };
  };

  // EVERY ELIGIBLE PART'S CROPS IN ONE LIST, for the column that draws them all — and each
  // one's colour beside it, keyed by the crop itself. The objects come from the store and are
  // the very ones the cell draws, so identity is a safe key, and no crop has to carry a field
  // it only means for the length of one render.
  // THE PAGES' OWN, for the manual's row: the photographs and the manual are two rows now —
  // one is this colourway's pictures, the other the paper every colourway ships with.
  const allPageCrops = pageCols.flatMap((p) => pageCropsOf(p).map((c) => [p, c] as const));
  const allCrops = listingCols.flatMap((p) => listingCrops[p] ?? []);
  const allPageList = allPageCrops.map(([, c]) => c);
  const allCropTones = new Map<ListingCrop, string>([
    ...listingCols.flatMap(
      (p) => (listingCrops[p] ?? []).map((c) => [c, toneOfPart(p)] as [ListingCrop, string]),
    ),
    ...allPageCrops.map(([p, c]) => [c, toneOfPart(p)] as [ListingCrop, string]),
  ]);


  // ONE PART'S MANUAL CROPS: the listing-crops cell, on the steps' own pictures. A screw is
  // never in a photograph of the assembled chair — it is inside the joint — and it is drawn in
  // the steps, which is where the manual call looks; a component is drawn there too.
  const pageCell = (i: number) => (
    <ListingCropCell
      crops={pageCropsOf(i)}
      photos={pagePhotos}
      asked={pageAsked.has(i)}
      // The crops alone: see cropsOnly.
      cropsOnly
      // NO WAITING SET HERE, and that is not the same omission its
      // neighbour avoided. The photograph half knows its pictures before it
      // starts — the eight listing shots — so it can draw the ones that have
      // answered and spin the rest. A step's crops land on whatever pages the
      // step spans, so this row does not know which pictures it will be
      // drawing until they arrive.
      //
      // What it DOES know is that the run is out, which is `pending`: the
      // pages found so far are drawn and one spinner sits after them until
      // the last step answers. Letting the tiles merely appear was the same
      // report minus the only part of it a cell that is still empty can
      // make — a run empties the store before it starts, so for the first
      // seconds every one of these columns looked like a finished search
      // that found nothing.
      pending={pageBusy}
      toneOf={toneOfCrop(i)}
      // THE PART TRAVELS WITH THE PAGE, exactly as it does on the half of
      // this row above. It did not, and that cost the zoom both of the
      // things it is for: no box on the picture (a box is about a part and
      // a picture together), and arrows that fell through to the table
      // underneath and walked to another part — when the set beside this
      // picture is the other PAGES in this cell.
      onZoom={(url) => setZoom({ src: url, part: i })}
      alt={items[i].nameEn || items[i].name || t('notice.parts.piece')}
    />
  );

  // Transposed: one row per field (the labels are the sticky first column), one
  // column per part, scrolling horizontally. The sticky column's right edge is a
  // box-shadow (a border vanishes on a sticky cell while the row scrolls under).
  //
  // RULED ON EVERY SIDE, like every table in the app (see TableCard): the look is TABLE_HEAD's
  // and TABLE_CELL's, and the sticky column keeps its shadow for the edge a collapsed border
  // leaves behind when the row scrolls under it.
  const labelCellCls =
    `sticky left-0 z-20 w-40 bg-white shadow-[inset_-2px_0_0_0_#cbd5e1] ${TABLE_HEAD}`;
  const dataCellCls = `min-w-[8rem] ${TABLE_CELL}`;
  const cellCls = (extra = '') => `${dataCellCls} ${extra}`;

  // A field value the manual didn't print (null/blank, or a bare dash the model
  // returned to mean "none") shows as an empty cell, so no stray dash is drawn
  // and mono/sans rows don't look inconsistent.
  const clean = (v: string | null) => (v && !/^[\s\-–—]*$/.test(v) ? v : '');

  // THE name, in the language being read.
  //
  // The model's, because the model was given the printed one and asked to better it:
  // it fixes the misprints ("FLET PAD"), unshouts the shouting, and names the four big
  // parts this manual never named at all. Two rows showed the question; one row is the
  // answer.
  //
  // The printed name is the fallback, not a rival — for an extraction from before the
  // model was asked to name anything, and for the odd part it declines to name. Still
  // unshouted, because that much is true of it whoever is reading.
  const partName = (p: PartItem) =>
    (language === 'fr' ? p.nameFr : p.nameEn) || formatPartName(p.name ?? '');

  // A field row: the label, then one cell per shown part with the value (empty where this
  // manual doesn't print it for that part).
  //
  // A row no shown part answered is not rendered at all. The table used to show every
  // field always, so its shape would be the same across manuals — but nobody reads two
  // manuals side by side, and what that bought in the abstract it paid for on every
  // real one: this manual prints no references and packs no bags, so CODE RÉF. and
  // SACHET were two empty rows asking to be read. An absent row says the same thing,
  // and says it in no space at all.
  // `always` draws the row even when every cell is empty. Off by default, because a
  // manual that prints no bags should not show a BAG row — the empty rows are how the
  // table says what this manual is like. On for size, where empty means something else
  // entirely: not "this manual has no sizes" but "nobody has read them yet", and a row
  // that is missing cannot say so.
  // `valueCls` styles the VALUE rather than the cell, inside an inline-block. It is how a
  // multi-line value keeps its own left alignment — the keys of a size lining up down the
  // column — while the block of them sits centred in the cell like every other row. Left
  // on the cell, the size column hugged the border of a 13rem column and read as a
  // different kind of row.
  // `underLabel` is a control belonging to the ROW, drawn beneath its name. The label
  // cell is the only place in the table that is not one part's answer, so a switch that
  // changes how every cell of one row reads has nowhere else to go: in a cell it would be
  // one part's, and above the table it would be the table's.
  // `product` is the whole product's cell in this row, or null to leave it blank — see
  // ProductColumn. Drawn through the same `valueCls` as the parts, so a size reads alike.
  const textRow = (
    label: ReactNode, get: (p: PartItem, i: number) => string | null, extra = '', always = false,
    valueCls = '', underLabel: ReactNode = null,
    underValue: ((p: PartItem, i: number) => ReactNode) | null = null,
    product: ProductCell | null = null,
  ) => {
    // The product column asks nothing of `get`, which is written against a part that column
    // has not got — it has `product` instead. Only the parts decide whether a row appears:
    // a row about parts that none of them answers is not made worth drawing by the product.
    const values = new Map(partCols.map((i) => [i, clean(get(items[i], i))]));
    // A row this manual has nothing for stays absent through a re-read too. It could come
    // back filled — a reference the first pass missed — but drawing CODE RÉF. and SACHET
    // as two spinners on every re-read, only to drop them again a moment later, is a table
    // that jumps to say "still nothing", which is what their absence already said.
    if (!always && ![...values.values()].some(Boolean)) return null;
    return (
      <tr>
        <th className={labelCellCls}>
          {label}
          {underLabel}
        </th>
        {cols.map((i) => (
          <td key={i} className={cellCls(extra)}>
            {i === ALL_PARTS ? product && (
              <>
                {valueCls
                  ? <span className={`mx-auto block w-fit ${valueCls}`}>{product.value}</span>
                  : product.value}
                {product.under}
              </>
            ) : reread(i) ? <Waiting /> : (
              <>
                {/* `w-fit` and auto margins, not an inline-block leaning on the cell's
                    text-align. Both centre a shrink-to-fit box in principle; only this one
                    does it independently of how the text inside is aligned, and the one
                    row that uses this is the one whose lines are deliberately NOT centred.
                    A block that has to be the same width as its longest line, sitting in
                    the middle of the cell, is the thing being asked for — so it is what
                    the CSS says. */}
                {valueCls
                  ? <span className={`mx-auto block w-fit ${valueCls}`}>{values.get(i)}</span>
                  : values.get(i)}
                {underValue?.(items[i], i)}
              </>
            )}
          </td>
        ))}
      </tr>
    );
  };

  return (
    <>
    <TableCard title={t('notice.parts.tableTitle')} className="max-w-5xl">
      <div
        ref={scrollRef}
        style={{ transform: 'rotateX(180deg)' }}
        // Grabbable only when there is somewhere to drag to. One column fits, and a grab
        // cursor over a table that cannot move is an affordance for nothing.
        className={`min-w-0 overflow-x-auto ${cols.length > 1 ? 'cursor-grab' : ''}`}
      >
        {/* The container is flipped so its horizontal scrollbar sits at the TOP;
            the table is flipped back so its content stays upright. */}
        <table style={{ transform: 'rotateX(180deg)' }} className={TABLE}>
          <tbody>
            {/* EVERYTHING THE MANUAL SAYS ABOUT THE PART, or none of it. The two picture
                rows below moved to the phase that reconstructs the mesh out of them, and
                they took the header with them — a strip of pictures with no name over it is
                a strip of pictures. So the same table draws its whole self in the parts
                phase and its last two rows in the mesh one. */}
            {!imagesOnly && (
            <>
            {/* The model answers the category in English — it is a key into the Parts
                DB ontology, not prose — so FR reads it back through the ontology's own
                French name, exactly as the info table and the Parts DB tab do. */}
            {textRow(
              st('notice.parts.category'),
              (p, i) => localizeCategoryOrBlank(cats, partCategory(p, i), language),
              'text-xs font-semibold uppercase tracking-wide text-slate-900',
              false, '', null, null,
              product.category,
            )}
            {/* THE NAME, AND THE ONE ROW YOU CAN CORRECT BY TYPING.
                Every other correction in this table is a choice between things the run
                found — a box, a category, a material off a list. A name has no list: when
                a manual supplies one screwdriver and calls for another it does not supply,
                the read names both of them "Screwdriver" and is not wrong to. Two columns
                headed the same word is a table nobody can use, and no re-run fixes it,
                because the page says no more than that. So it is typed.
                Always drawn, unlike the read-only rows: a part the run declined to name has
                to have somewhere to be named. */}
            <tr>
              <th className={labelCellCls}>{st('notice.parts.name')}</th>
              {cols.map((i) => (
                <td key={i} className={cellCls()}>
                  {/* The product's name is the listing's, read-only: it is renamed where the
                      listing is read, not here. */}
                  {i === ALL_PARTS ? product.name : reread(i) ? <Waiting /> : renaming?.at === i ? (
                    <input
                      autoFocus
                      value={renaming.draft}
                      onChange={(e) => setRenaming({ at: i, draft: e.target.value })}
                      onFocus={(e) => e.currentTarget.select()}
                      onBlur={() => {
                        // Escape already put the old name back; the blur it causes must not
                        // write the draft it was abandoning.
                        if (escaped.current) escaped.current = false;
                        else setPartName(docId, i, language, renaming.draft);
                        setRenaming(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                        if (e.key === 'Escape') {
                          escaped.current = true;
                          e.currentTarget.blur();
                        }
                      }}
                      className="w-full rounded-md border border-blue-500 bg-white px-2 py-1 text-center text-sm text-slate-900 outline-none"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setRenaming({ at: i, draft: partName(items[i]) })}
                      title={t('notice.parts.rename')}
                      className="mx-auto block rounded-md px-2 py-1 text-center underline decoration-dotted decoration-slate-400 underline-offset-4 transition-colors hover:bg-slate-100 hover:decoration-slate-600"
                    >
                      {clean(partName(items[i])) || (
                        <span className="text-slate-400">{st('notice.parts.rename')}</span>
                      )}
                    </button>
                  )}
                </td>
              ))}
            </tr>

            {/* Whether the reusable catalog already knows this part — and, when it
                does not, the button that fixes that. One cell, because they are one
                thought: "non" is not a fact to record, it is the reason the button is
                there, and "oui" is the reason there is no button. */}
            <tr>
              <th className={labelCellCls}>
                {st('notice.parts.inDb')}
                <AddAllToDb todo={missing} saving={saveAll} />
              </th>
              {cols.map((i) => (
                <td
                  key={i}
                  className={cellCls('text-xs font-semibold uppercase tracking-wide')}
                >
                  <div className="flex flex-col items-center gap-1.5">
                    {/* The re-read decides this too: it is the call that matches a part
                        against the catalogue, so OUI and the button that would fix NON
                        are both answers still being worked out. */}
                    {/* THE WHOLE PRODUCT: in the database when one of its models for this
                        colour is switched on in Phase 5, and the link is to that row of the
                        Database page. No add button — adding is that switch, on a model. */}
                    {i === ALL_PARTS ? (
                      productInDb === null ? <Waiting /> : productInDb ? (
                        <Link
                          to={`/catalog?product=${encodeURIComponent(docId)}&submodel=${encodeURIComponent(submodel)}&from=${from}`}
                          title={t('notice.parts.seeInDb')}
                          className="inline-flex items-center gap-1 text-blue-600 underline decoration-blue-600/40 underline-offset-2 transition-colors hover:decoration-blue-600"
                        >
                          {st('notice.parts.yes')}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      ) : (
                        <span className="text-rose-600">{st('notice.parts.no')}</span>
                      )
                    ) : reread(i) ? <Waiting /> : known(items[i], i) != null ? (
                      // OUI is the claim; the link is how you check it. The model
                      // decided this part is that row, and a match nobody can look at
                      // is a match nobody can correct.
                      <Link
                        to={`/catalog?item=${known(items[i], i)}&from=${from}`}
                        title={t('notice.parts.seeInDb')}
                        className="inline-flex items-center gap-1 text-blue-600 underline decoration-blue-600/40 underline-offset-2 transition-colors hover:decoration-blue-600"
                      >
                        {st('notice.parts.yes')}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    ) : (
                      <>
                        <span className="text-rose-600">{st('notice.parts.no')}</span>
                        <button
                          type="button"
                          onClick={() => setAddingIdx(i)}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium normal-case tracking-normal text-slate-600 transition-colors hover:border-blue-500 hover:text-blue-600"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          {st('notice.parts.addToDb')}
                        </button>
                      </>
                    )}
                  </div>
                </td>
              ))}
            </tr>

            {/* What the part physically is: shape, material, finish, holes. English only,
                and deliberately: its reader is the video prompt, which is always English,
                and a French copy nothing sends would be a second answer to drift from the
                first.

                Above the measurements: it is the sentence that says what you are looking
                at, and the numbers are read against it. No size in here — the row below
                carries that, measured rather than guessed. */}
            {textRow(
              st('notice.parts.description'),
              (p) => p.description ?? '',
              // Centred like every other row. It reads as a sentence and not as a value,
              // which is the argument for having set it left, but the argument the other
              // way is the column: one part per column, and a sentence hugging its left
              // border while the name and the category above it sit centred looked like a
              // cell that had come loose rather than like prose.
              'whitespace-normal text-xs leading-snug text-slate-600',
            )}
            {/* What this MANUAL says about the part, as opposed to what the part is: a
                letter and a quantity mean something only inside this notice, a bag is
                the bag this box was packed in, and a reference is the number this
                manufacturer prints. None of them describe a part well enough to keep,
                so they close the written half rather than sit among the rows that do. */}
            {textRow(st('notice.parts.ref'), (p) => p.ref, 'font-mono')}
            {textRow(st('notice.parts.bag'), (p) => p.bag)}
            {/* The bare number: the row is already labelled QUANTITÉ, and the "x" the
                manual prints next to it is the manual saying what this column says.

                Above the letter, which is the written half's last word on purpose: how many
                there are is asked of every manual, and a letter is asked of the few that
                print one — so the row most parts fill comes first and the one most of them
                leave blank closes. */}
            {textRow(
              st('notice.parts.qty'),
              (p) => (p.qty != null ? String(p.qty) : null),
              'font-semibold tabular-nums text-slate-900',
            )}
            {/* ONE OBJECT REPEATED, OR A MIRROR PAIR — directly under the count, because it
                is a question about the count and unreadable away from it: "Left/Right" over a
                part supplied singly would be a claim about nothing. See symmetryOf, which
                answers it from the count where nobody has said.

                ALWAYS DRAWN, unlike the rows above it. Its commonest value is NONE, and a row
                that vanished whenever every part answered NONE would be absent from exactly
                the manuals where "nothing here is handed" is the thing worth knowing — and
                present only on the ones where you would have spotted the pair anyway.

                AND CHOSEN BY HAND, which makes it the second row here you can correct without
                re-running anything. Whether two rails are mirror images is a fact about the
                drawing that a reader with the manual open sees at a glance, and the run reads
                out of a small grey picture: of the two, the run is the one guessing. There is
                no list to search and no call to make — three words, one of them right.

                A NATIVE SELECT, for the reason the listing strip's is (see ListingGallery):
                this table sits inside an `overflow-x-auto`, so a menu drawn in the page would
                be clipped by the very element that lets the table slide. The browser draws its
                own list outside the page, and it arrives with the keyboard and the touch
                behaviour already done.

                Styled as the value it replaces — no arrow, no border until the pointer is on
                it — so the row still reads as an answer and not as a form control, which is
                what the dozen rows around it read as. */}
            <tr>
              <th className={labelCellCls}>{st('notice.parts.symmetry')}</th>
              {cols.map((i) => {
                const sym = i === ALL_PARTS ? null : symmetryOf(items[i]);
                return (
                  <td key={i} className={cellCls()}>
                    {i === ALL_PARTS ? null : reread(i) ? <Waiting /> : (
                      <select
                        value={sym ?? ''}
                        onChange={(e) => setPartSymmetry(docId, i, e.target.value as Symmetry)}
                        title={t('notice.parts.symmetryPick')}
                        aria-label={t('notice.parts.symmetryPick')}
                        className="mx-auto block cursor-pointer appearance-none rounded-md bg-none px-2 py-1 text-center text-xs font-semibold uppercase tracking-wide text-slate-900 transition-colors hover:ring-1 hover:ring-blue-400"
                      >
                        {/* NOBODY HAS SAID, and it is selectable only while that is true. An
                            even quantity the run left unclassified is a real state and the
                            cell has to be able to show it — but it is not an answer anybody
                            should be able to pick, so it leaves the list the moment there
                            is one. */}
                        {!sym && <option value="" disabled>—</option>}
                        {SYMMETRIES.map((v) => (
                          <option key={v} value={v}>{t(`notice.parts.sym.${v}`)}</option>
                        ))}
                      </select>
                    )}
                  </td>
                );
              })}
            </tr>
            {textRow(st('notice.parts.letter'), (p) => p.letter, 'font-semibold text-slate-900')}

            {/* IN THE BOX, OR NOT — and the row exists because the answer is not always
                yes. A manual's supplied section lists what the carton holds; a step can
                still call for a screwdriver that is nowhere in it, and a parts list built
                from that section alone had no way to carry one. The read adds those tools
                now, marked here, and this row is where somebody with the box open
                disagrees with it.

                ALWAYS DRAWN, unlike the rows above: "everything is supplied" is an answer
                worth seeing, and a row that appeared only when something was missing would
                make its own absence the news. */}
            <tr>
              <th className={labelCellCls}>{st('notice.parts.supplied')}</th>
              {cols.map((i) => {
                const yes = i !== ALL_PARTS && items[i]?.supplied !== false;
                return (
                  <td key={i} className={cellCls()}>
                    {i === ALL_PARTS ? null : reread(i) ? <Waiting /> : (
                      <button
                        type="button"
                        onClick={() => setPartSupplied(docId, i, !yes)}
                        title={t('notice.parts.suppliedPick')}
                        aria-pressed={yes}
                        className={`mx-auto flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
                          yes
                            ? 'border-slate-300 text-slate-600 hover:border-blue-500 hover:text-blue-600'
                            : 'border-amber-400 text-amber-600 hover:border-amber-500'
                        }`}
                      >
                        {yes ? st('notice.parts.suppliedYes') : st('notice.parts.suppliedNo')}
                      </button>
                    )}
                  </td>
                );
              })}
            </tr>

            {/* The pictures, in the order they are made: the manual's own drawing, the
                finished-product photographs offered beside it, and the render those two
                produced. No seam over them: one grid, ruled like every other table. */}
            <tr>
              {/* Its Redo and its badge are beside the phase button — see SchemaBoxesInfo. */}
              <th className={labelCellCls}>{st('notice.parts.piece')}</th>
              {/* The cell is the drawing and nothing else now. It used to carry a Redo of
                  its own under the picture, which is what the h-px / h-full height trick
                  was for: a td has no definite height, and without it the buttons sat at
                  different levels across the row. Both are gone with the button. */}
              {cols.map((i) => (
                <td key={i} className={cellCls()}>
                  {/* A part with no box has no drawing here, and says so rather than
                      waiting for one: the "…" below means a crop that has not been cut
                      yet, and a tool the carton does not contain will never have one. */}
                  {i === ALL_PARTS ? null : reread(i) ? <Waiting />
                    : !hasBox(items[i]?.bbox) ? (
                    <span className="text-slate-400">{'—'}</span>
                  ) : crops[i] ? (
                    <img
                      src={crops[i]}
                      alt={items[i].ref ?? t('notice.parts.piece')}
                      onClick={() => setZoom({ src: crops[i]!, shot: `p${i}` })}
                      className={`mx-auto max-h-24 w-auto cursor-zoom-in rounded bg-white transition-opacity hover:opacity-90 ${
                        reboxing === ALL_REREAD || reboxing === i ? 'opacity-40' : ''
                      }`}
                    />
                  ) : (
                    <span className="text-slate-400">…</span>
                  )}
                </td>
              ))}
            </tr>

            {/* WHERE THE PART ACTUALLY IS, in the photographs of the assembled product.
                Between the manual's drawing above and the generated photograph below,
                because that is where it sits in the work: it is cut from the listing, and
                for anything visible from outside it is the better picture of the two — the
                real object, in real light, in its real finish.

                MOST PARTS HAVE NOTHING HERE and that is the answer. The furniture in those
                photographs is assembled: the screws are driven, the dowels are in the
                joints, the tools are away. A row with three cells filled out of fourteen is
                a good run, so the empty ones say nothing at all rather than apologising. */}
            {drawsListing && (
              <tr>
                {/* Its two presses — finding the boxes and drawing the masks — and their
                    badges are beside the phase button: see ListingCropsInfo and MasksInfo. */}
                <th className={labelCellCls}>{st('notice.parts.listingCrops')}</th>
                {cols.map((i) => (
                  <td key={i} className={cellCls()}>
                    {i === ALL_PARTS ? (
                      /* EVERY PART ON THE SAME FRAME. The columns beside it answer "where is
                         this part", one part per column, and cannot answer "what did the run
                         make of this picture" — for that the answers have to share a frame,
                         and they were only ever on eight separate ones.
                         Which parts each shape is belongs to the zoom: eight labels on a
                         120-pixel tile is a green rectangle with writing on it. */
                      <div className="flex flex-col items-center gap-1">
                        <ListingCropCell
                          crops={allCrops}
                          photos={listingPhotos}
                          asked={listingCols.some((p) => cropsAsked.has(p))}
                          waiting={comingCrops ?? cropsWaiting ?? masksWaiting(allCrops)}
                          toneOf={(c) => allCropTones.get(c)}
                          onZoom={(url) => setZoom({ src: url, part: ALL_PARTS })}
                          alt={t('notice.parts.allParts')}
                        />
                      </div>
                    ) : asksListing(i) ? (
                      /* PHOTOGRAPH BY PHOTOGRAPH while the run is out, rather than one
                         spinner over the cell until the last of eight calls lands.

                         What it must not do is leave the PREVIOUS run's crops sitting there
                         looking settled — they are about to be replaced, and on photographs
                         that may not even be the eight being read. That is why this was a
                         spinner. It is not why it has to stay one: a run empties what it is
                         about to answer for before it starts (see runListingCrops), so what
                         is in the store during a run is this run's, and the only thing
                         missing was which tiles had come back. `read` says so, so the tiles
                         that have answered are drawn and the rest wait.

                         Every eligible column shown is in the run: a scoped run only
                         happens on a part's own tab, where its column is the only one. */
                        <ListingCropCell
                          crops={listingCrops[i] ?? []}
                          photos={listingPhotos}
                          asked={cropsAsked.has(i)}
                          waiting={comingCrops ?? cropsWaiting ?? masksWaiting(listingCrops[i] ?? [])}
                          // Its own colour here too, so the column and the ALL PARTS tile
                          // beside it are visibly about the same part.
                          toneOf={toneOfCrop(i)}
                          // THE FEW KEPT, NUMBERED ON THEIR BOXES: about two to four per part,
                          // chosen for different sides and the largest boxes (see
                          // listingSelectRun), largest first. They were a row of
                          // their own under this one, and every picture in it was already here.
                          rankOf={(c) => rankIn(selection.selected[i], c)}
                          onZoom={(url) => setZoom({ src: url, part: i })}
                          alt={items[i].nameEn || items[i].name || t('notice.parts.piece')}
                        />
                    ) : null}
                  </td>
                ))}
              </tr>
            )}

            {/* WHERE THE MANUAL DRAWS EACH PART, a row of its own under the photographs'. They
                were one row, and the two are different things: the photographs are this
                colourway's, the manual is the same paper in every box. Same calls, same store —
                only drawn apart. A component is looked for in both rows; hardware and tools,
                which no photograph shows, in this one alone. */}
            {drawsPages && (
              <tr>
                <th className={labelCellCls}>{st('notice.parts.manualCrops')}</th>
                {cols.map((i) => (
                  <td key={i} className={cellCls()}>
                    {i === ALL_PARTS ? (
                      <div className="flex flex-col items-center gap-1">
                        <ListingCropCell
                          crops={allPageList}
                          photos={pagePhotos}
                          asked={pageCols.some((p) => pageAsked.has(p))}
                          pending={pageBusy}
                          toneOf={(c) => allCropTones.get(c)}
                          onZoom={(url) => setZoom({ src: url, part: ALL_PARTS })}
                          alt={t('notice.parts.allParts')}
                        />
                      </div>
                    ) : asksPage(i) ? pageCell(i) : null}
                  </td>
                ))}
              </tr>
            )}

            {/* How big the part is: one row, one attribute, two possible answers. The
                manual's printed spec for hardware ("M6 x 70 mm" says everything about a
                bolt), the measured box for the pieces the manual gives no size at all —
                which is most of the furniture, and the reason the dimension read exists.

                They were four rows for a while, one per axis plus the spec. At most one
                of them ever had a value in a given column, so it was three rows of
                whitespace saying which of our two sources happened to know. */}
            {textRow(
              st('notice.parts.size'),
              (p, i) => (sizing && !p.size?.spec
                ? '…'
                : formatPartSize(p.size, partCategory(p, i))),
              // pre so the newlines are lines AND a line never wraps: "width: 453 → 482 mm"
              // broken after its arrow reads as two answers. The column widens instead. The
              // block is centred in the cell and its lines are left-aligned within it, so the
              // keys still line up down the column and the values compare across parts.
              'whitespace-pre font-mono text-[11px] leading-relaxed',
              true,
              'text-left',
              // WHAT IS UNDER THE LABEL IS THE BUTTON, and it was one per cell. Fourteen
              // copies of one sentence across a row, each acting on its own column — where
              // every other run in this table is a single press in this column, scoped by
              // the tab. The cells keep what is theirs: the badge that reads back one
              // part's prompt and reasoning, and the error when its own call failed.
              // Its press and its badge are beside the phase button — see EstimateInfo.
              null,
              // Under a cell with NOTHING in it, and only there. The two reading calls
              // have already been and found no size printed for this part and none drawn
              // on the listing; this is the only thing left to try, and a part that has
              // an answer has nothing to gain from a worse one.
              (p, i) => (
                <>
                {/* A SCREW'S POINT, which decides whether any part is bored for it (see ScrewTip). */}
                <ScrewTip docId={docId} index={i} part={p} />
                {/* HOW BIG THIS PART IS ACCORDING TO THE PHOTOGRAPHS, one row per picture
                    that can measure it — no call, just the picture's scale times the part's
                    box. See scaleEstimates, which is where the whole argument lives.
                    Only for a part nothing has measured: with a size of its own there is a
                    real answer in the cell already, and a column of readings under it would
                    be four guesses arguing with a fact. */}
                {noMeasurements(p.size) && (() => {
                  const read = scaleEstimates(docId, i, pixels);
                  if (!read.length) return null;
                  return (
                    <table className="mx-auto mt-2 w-full border-collapse border border-slate-300 text-center text-[10px] tabular-nums">
                      <tbody>
                        {read.map(({ url, view, axes, across, up }) => (
                          <tr key={url}>
                            <td className="border border-slate-300 px-1 py-0.5 align-middle font-semibold text-slate-500">
                              {directionLabel(view)}
                            </td>
                            {/* The word with the number, because which dimension a column
                                holds changes with the row: a front view measures the width
                                across and a side view the depth. */}
                            <td className="border border-slate-300 px-1 py-0.5 align-middle">
                              {across == null ? '' : `${sizeKeyLabel(axes.across)} ${across}`}
                            </td>
                            <td className="border border-slate-300 px-1 py-0.5 align-middle">
                              {up == null ? '' : `${sizeKeyLabel(axes.up)} ${up}`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
                {wantsEstimate(p, partCategory(p, i)) ? (
                <div className="mt-2 flex flex-col items-center gap-1">
                  <div className="flex items-center gap-1.5">
                  {/* THIS PART'S OWN CALL IS OUT, OR COMING, said in this part's own cell —
                      the button is one press for the whole row now, and a fraction in the
                      label column cannot say WHICH of five columns is being answered.
                      Every part the press still owes spins, not only the one in flight: they
                      go one at a time, and a cell that is next in the queue is a cell whose
                      size is about to be replaced. Dimmer while it is merely queued, so the
                      row still says which one is actually out. */}
                  {(estimates.current === i || estimates.queued.includes(i)) && (
                    <Loader2 className={`h-3 w-3 shrink-0 animate-spin ${
                      estimates.current === i ? 'text-slate-400' : 'text-slate-500/40'
                    }`}
                    />
                  )}
                  {/* NO BADGE HERE. The prompt and the reasoning are read from under the
                      button in the label column, where the press is — a copy in every cell
                      was the same two rows offered fourteen times across one row. */}
                  </div>
                  {estimates.failed.includes(i) && (
                    <span className="text-[10px] text-rose-500">
                      {t('notice.parts.estimateFailed')}
                    </span>
                  )}
                </div>
              ) : null}
                </>
              ),
              product.size,
            )}
            </>
            )}
            {/* The pictures the generator was GIVEN, and then the photographs it produced
                — in that order, because that is the order the work happens in and each row
                is made from the one above it.

                Both are drawn only when there is a generator on the page. The table is also
                the extraction's own answer, shown the moment the list comes back and before
                anything has been drawn for it; two empty rows there would be two questions
                the phase had not been asked yet. */}
            {imageCols && (
              <>
                {/* The context row goes entirely on hardware and tools. Those pictures are
                    the product's own photographs — the chair from six angles — and they
                    are there to tell a generator what the wood and the fabric look like on
                    the piece it is drawing. A screw, a dowel or an allen key is the same
                    object whatever the chair is made of, so every one of those tiles is a
                    picture of something else, and offering ten of them to be ticked on is
                    offering a decision with no right answer.

                    Only when EVERY part shown is one: the table draws a column per part,
                    and a row dropped because one of five columns had nothing to put in it
                    would take the other four's pictures with it. */}
                {!cols.every((i) => items[i] && isHardwareOrTool(items[i])) && (
                <tr>
                  <th className={labelCellCls}>{st('notice.step3.context')}</th>
                  {/* The tiles wrap against the CELL, with nothing narrowing it. They used
                      to sit in a 13rem block: that was the width of a column back when
                      there was one per part, and it survived into a table with a single
                      wide column as a strip of thumbnails down the middle of it, three to
                      a row, with half the row empty on either side. */}
                  {cols.map((i) => (
                    <td key={i} className={cellCls('align-middle')}>
                      {imageCols[i] && (
                        <ContextCell
                          context={imageCols[i].context}
                          onZoom={(url) => onZoomImage?.(url)}
                          onToggleContext={(url) => onToggleContext?.(url)}
                        />
                      )}
                    </td>
                  ))}
                </tr>
                )}
                <tr>
                  {/* THE ROW'S OWN CONTROLS, under its title, like every other run in this
                      table: what it does, the button that does it, and the prompt that press
                      sends. They were under the pictures inside the cell, which put the one
                      control of the row below the tallest thing in it and drew the prompt
                      again for every column — thirteen identical badges for one instruction.

                      The button is here only when there is ONE part on screen, because then
                      the row is that part. Under "All parts" each column is a different part
                      and a single button could not say which one it would draw, so each cell
                      keeps its own — see below. The prompt is the row's either way: it is the
                      same instruction for every column. */}
                  <th className={labelCellCls}>
                    <div className="flex flex-col items-center gap-1.5">
                      <span>{st('notice.step3.generated')}</span>
                      {onePart && imageCols[partCols[0]] && (
                        <GenerateButton
                          col={imageCols[partCols[0]]}
                          at={genPick(partCols[0])}
                          onRegenerate={(img) => onRegenerate?.(partCols[0], img)}
                        />
                      )}
                      <PromptTooltip />
                    </div>
                  </th>
                  {cols.map((i) => (
                    <td key={i} className={cellCls('align-middle')}>
                      {imageCols[i] && (
                        <>
                          <GeneratedCell
                            col={imageCols[i]}
                            at={genPick(i)}
                            onSelect={(at) => setGenAt((m) => ({ ...m, [i]: at }))}
                            onZoom={(url) => onZoomImage?.(url)}
                            onToggleDb={(historyIndex, img, added) =>
                              onToggleDb?.(i, historyIndex, img, added)}
                          />
                          {!onePart && (
                            <div className="mt-2 flex justify-center">
                              <GenerateButton
                                col={imageCols[i]}
                                at={genPick(i)}
                                onRegenerate={(img) => onRegenerate?.(i, img)}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  ))}
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
      </TableCard>
      {addingIdx !== null && items[addingIdx] && (
        <CatalogItemModal
          title={t('notice.catalog.addTitle')}
          // The same names the table shows, in the same order of authority — the catalog is
          // not the place to reopen a question the table has settled.
          initial={defaultFields(items[addingIdx])}
          // The part as THIS manual draws it, cropped from the page — the drawing this
          // save adds to the catalog. Tagged with docId, so the catalog remembers which
          // manual it came from and a part found in two manuals keeps both drawings.
          newSchematic={crops[addingIdx] ?? null}
          onSave={async (fields) => {
            const id = await savePartToDb(docId, items[addingIdx], crops[addingIdx] ?? null, fields);
            setAdded((m) => new Map(m).set(addingIdx, id));
            // The catalog just changed: drop the cached copy so every other part on
            // this page re-asks. Two rows of one manual are often the same part.
            invalidateCatalogItems();
            setAddingIdx(null);
          }}
          onClose={() => setAddingIdx(null)}
        />
      )}

      {/* ← and → walk the eight photographs while one is open, which is the only way to
          compare two of them: you cannot hold a chair from two angles in your head at
          thumbnail size. The box is recomputed for each one, so what steps is the PART
          through the pictures — found here, not found there — rather than a slideshow that
          happens to have a rectangle stuck on the first frame.

          Only for a listing photograph. A part's drawing is one picture with nothing to
          step to, and the arrow keys stay the manual reader's underneath. */}
      <ImageLightbox
        src={zoom?.src ?? null}
        // TWO GALLERIES THROUGH ONE ZOOM, because there are two kinds of picture in this
        // table and a reader stepping through one has no business landing in the other: the
        // listing photographs of one part (identified by URL — each is a different picture
        // of the same thing), and the drawings across the SCHEMA row (identified by column,
        // since two of them can be the same pixels). Whichever the open one belongs to is
        // the set the arrows walk.
        srcs={zoomPart != null ? zoomGallery : zoom?.shot ? schemaShots.map((s) => s.src) : undefined}
        stepIds={zoomPart == null && zoom?.shot ? shotIds : undefined}
        stepId={zoomPart == null ? zoom?.shot : undefined}
        onNavigate={zoomPart != null
          ? (url) => setZoom({ src: url, part: zoomPart })
          : zoom?.shot
            ? (id) => {
              const next = schemaShots.find((s) => s.id === id);
              if (next) setZoom({ src: next.src, shot: next.id });
            }
            : undefined}
        boxes={drawnBoxes(
          zoomCrops, partAlt, toneOfPart, showBoxes, showMasks, hiddenParts, zoomRaw,
          hiddenRings, boxSize, (d) => t(`notice.parts.depth.${d}`),
        )}
        // What the picture is OF, beside the counter — the classification Phase 1 made and
        // four other things already read. Only for a listing photograph: a drawing of a part
        // has no side of the product to show.
        view={zoomPart != null && zoom && viewOfPhoto.get(zoom.src)
          ? directionLabel(viewOfPhoto.get(zoom.src) as string)
          : undefined}
        // Open on a photograph the run has not reached yet and it is the tile's own state,
        // larger: dimmed, with a spinner. Not "no parts found here" — nobody has looked.
        waiting={!!zoom && (!!cropsWaiting?.has(zoom.src)
          || !!masksWaiting(zoomCrops.map(({ crop }) => crop))?.has(zoom.src))}
        // NOT ON A PAGE. Striking a box through writes to the listing store, keyed by the
        // photograph's URL — a page crop lives in the other store and would be written to a
        // part's listing crops, where nothing would ever read it back.
        onToggleBox={zoomPart == null || !zoom || zoomOnPage
          ? undefined
          : (at) => {
            // Struck through on the part it belongs to, which is not where it sits in this
            // list: from the ALL PARTS column the boxes on one photograph come from several
            // parts, and each part counts its own. See CropAt.
            const c = zoomCrops[at];
            if (c) toggleListingCrop(docId, c.part, zoom.src, c.nth);
          }}
        // WHAT THE CALL SAID ABOUT THIS PHOTOGRAPH, behind the badge every other call in
        // this app is read from — the same icon, the same two-line menu, the same reading
        // window that scrolls and copies. The reasoning and the raw list of polygon
        // coordinates used to be PRINTED here, which put four hundred lines of text in the
        // one place somebody had opened to LOOK at something.
        //
        // In the panel, under the counter, and not out in the window's corner: the corner
        // is where a control that acts on the picture goes (see ImageLightbox), a long way
        // from the picture and from the eye — this belongs to what is on screen, so it sits
        // with the other thing that describes it, the "3 / 8".
        //
        // TWO CALLS MADE THIS PICTURE, so there are two badges and each says which it is.
        // BOXES is the one that found the parts — one call, this photograph, every part at
        // once. MASKS is the second pass, which is one call per rectangle. Unlabelled they
        // were two identical icons, and the difference between them is the whole reason the
        // shapes are better than the boxes.
        details={zoomNote && (
          <div className="flex flex-col items-center gap-2">
          {/* WHO IS ON THIS PICTURE, and each name in the colour its marks are drawn in —
              which makes the row a legend as well as a set of switches: the boxes carry
              their names, and the shapes under them carry nothing but a colour.
              Each part ONCE, however many times it was found: a part struck through goes
              off the picture entirely, and both of the wood buttons are that one claim. */}
          {/* WHAT THE PICTURE IMPLIES ABOUT ITS OWN SCALE, one row per part. Only on a
              square-on view, where the two edges are two dimensions — and only for parts
              with a number on the axis. A photograph has one scale per DEPTH, so these
              should agree within the perspective across the object; where two rows are half
              an order apart, one of the sizes or one of the boxes is wrong. */}
          {scales.length > 0 && (
            <div className="w-full max-w-sm">
              {/* RULED, inside and out. Three columns of numbers with nothing between them
                  is read by tracking a row across whitespace, which is exactly what a table
                  is for; `border-collapse` so the inner rules are single lines rather than
                  two borders side by side. Centred both ways — the cells hold a name and two
                  short numbers, and a column centred on its own heading needs no alignment
                  argued per cell. */}
              <table className="w-full border-collapse border border-slate-300 text-center text-[10px] tabular-nums">
                <thead className={TIP_LABEL}>
                  <tr>
                    <th className="border border-slate-300 px-2 py-1 align-middle font-medium">
                      {st('notice.parts.mmPerPx')}
                    </th>
                    <th className="border border-slate-300 px-2 py-1 align-middle font-medium">
                      {sizeKeyLabel(zoomAxes?.across ?? 'x')}
                    </th>
                    <th className="border border-slate-300 px-2 py-1 align-middle font-medium">
                      {sizeKeyLabel(zoomAxes?.up ?? 'z')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {scales.map(({ part, across, up }) => (
                    <tr key={part}>
                      <td
                        className="border border-slate-300 px-2 py-1 align-middle font-semibold"
                        style={{ color: toneOfPart(part) }}
                      >
                        {partAlt(part)}
                      </td>
                      {/* BLANK where nothing is known, not a dash. A dash is a mark, and a
                          column of marks reads as a column of answers. */}
                      <td className="border border-slate-300 px-2 py-1 align-middle text-slate-700">
                        {across == null ? '' : across.toFixed(3)}
                      </td>
                      <td className="border border-slate-300 px-2 py-1 align-middle text-slate-700">
                        {up == null ? '' : up.toFixed(3)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {/* THE PICTURE'S SCALE, under the parts it was read from. In the table's own
                    foot rather than as a fifth part: it is not a row about a part, and the
                    rule above it is what says so. */}
                <tfoot>
                  <tr>
                    <td className="border border-slate-600 px-2 py-1 align-middle font-semibold text-slate-400">
                      {st('notice.parts.mmPerPxMean')}
                    </td>
                    <td className="border border-slate-300 px-2 py-1 align-middle font-semibold text-slate-800">
                      {meanScale('across')?.toFixed(3) ?? ''}
                    </td>
                    <td className="border border-slate-300 px-2 py-1 align-middle font-semibold text-slate-800">
                      {meanScale('up')?.toFixed(3) ?? ''}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          {/* A LINE PER PART, and its polygons named along it. They shared one wrapping line
              while a name was all there was to say; a part is several shapes, and each of
              them is a switch — on one line they ran into the next part's, and which name
              owned which "Polygon 2" was decided by where the line happened to wrap.
              UNDER THE SCALE TABLE, which names the same parts in the same colours: the
              table is a reading of the picture and these are the switches that change it,
              so the reading comes first and the controls sit beneath it. */}
          {[...new Set(zoomCrops.map((c) => c.part))].length > 0 && (
            <div className="flex flex-col items-center gap-1">
              {[...new Set(zoomCrops.map((c) => c.part))].map((part) => {
                const on = !hiddenParts.has(part);
                // EVERY SHAPE THIS PART WAS TRACED AS ON THIS PICTURE, numbered along the
                // row rather than within its crop: a part found twice is one claim in the
                // legend above, so "Polygon 3" counts from the part's first shape whichever
                // of its rectangles that shape belongs to.
                const rings = zoomCrops
                  .filter((c) => c.part === part)
                  .flatMap(({ crop }) => cropRings(crop)
                    .map((_, ring) => ringKey(crop, ring)));
                return (
                  <div
                    key={part}
                    className="flex flex-wrap items-baseline justify-center gap-x-2 gap-y-0.5"
                  >
                    <button
                      type="button"
                      onClick={() => setHiddenParts((was) => {
                        const next = new Set(was);
                        if (!next.delete(part)) next.add(part);
                        return next;
                      })}
                      aria-pressed={on}
                      style={on ? { color: toneOfPart(part) } : undefined}
                      className={`text-[11px] font-semibold transition-opacity ${
                        on ? 'hover:opacity-70' : 'text-slate-500 line-through opacity-60'
                      }`}
                    >
                      {partAlt(part)}
                    </button>
                    {/* Only where there is more than one: a part traced as a single shape has
                        its name for that shape already, and "Polygon 1" beside it would be
                        the same switch offered twice. */}
                    {rings.length > 1 && rings.map((key, n) => {
                      // Dimmed with the part when the part itself is off — the shape is not
                      // on the picture either way, and a lit switch over a hidden shape is
                      // the one thing this row must not say.
                      const lit = on && !hiddenRings.has(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setHiddenRings((was) => {
                            const next = new Set(was);
                            if (!next.delete(key)) next.add(key);
                            return next;
                          })}
                          aria-pressed={lit}
                          style={lit ? { color: toneOfPart(part) } : undefined}
                          className={`text-[10px] font-medium transition-opacity ${
                            lit ? 'opacity-80 hover:opacity-60' : 'text-slate-500 line-through opacity-50'
                          }`}
                        >
                          {t('notice.parts.polygonNth').replace('{n}', String(n + 1))}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex items-start justify-center gap-6">
            <span className="flex flex-col items-center gap-2">
            <span className="flex items-center gap-1.5">
              {/* THE NAME IS THE SWITCH. It was a label beside a badge, and the two things
                  it can mean — "these marks are the boxes" and "show me the boxes" — are the
                  same sentence, so a second control saying it again would be the label
                  twice. Struck through when the marks are off, which is the mark this table
                  already uses for a detection somebody turned off. */}
              <MarkSwitch
                label={t('notice.parts.boxesTitle')}
                on={showBoxes}
                onToggle={() => setShowBoxes((was) => !was)}
              />
              <CallBadge
                promptLabel={t('notice.parts.listingPrompt')}
                // THIS photograph's call, so the prompt panel shows the one picture it
                // carried. The run posts them one at a time.
                load={() => listingCropsPrompt(docId, items, allCategories, crops, only, zoom?.src)}
                answer={{
                  label: t('notice.parts.listingAnswer'),
                  content: reasoningTooltip(zoomNote.reasoning, zoomNote.raw, t),
                }}
              />
            </span>
            {/* WHICH RECTANGLE, on its own line and centred under the word it belongs to —
                it changes what BOXES draws and nothing about MASKS, so it hangs from that
                half of the row rather than from the middle of both. The row is top-aligned,
                which is what keeps the two words level with a control under one of them.

                The two differ by the padding the server adds on the way in, and the
                difference is the question this pair answers: how much of a loose box is
                ours and how much is the answer's. Only where the reply it reads is still
                there to be read. */}
            {showBoxes && zoomNote && (
              <SegmentedToggle
                value={rawBoxes ? 'raw' : 'padded'}
                onChange={(v) => setRawBoxes(v === 'raw')}
                // RAW FIRST, because it is the answer: the padded one is that rectangle plus
                // arithmetic of ours, and a switch reads left to right as what came in and
                // what we did to it.
                options={[
                  { value: 'raw', label: t('notice.parts.boxesRaw') },
                  { value: 'padded', label: t('notice.parts.boxesPadded') },
                ]}
              />
            )}
            </span>
            {/* The boxes drawn on THIS picture, in the order the zoom draws them — which is
                the order their calls are listed in, so a section can be found by the shape
                it is about. A photograph the second pass never reached has none. */}
            {zoomCrops.length > 0 && (
            <span className="flex items-center gap-1.5">
              <MarkSwitch
                label={t('notice.parts.masksTitle')}
                on={showMasks}
                onToggle={() => setShowMasks((was) => !was)}
              />
              <MasksBadge
                boxes={zoomCrops.map(({ crop, part }) => ({
                  crop,
                  part,
                  name: partAlt(part),
                }))}
              />
            </span>
            )}
          </div>
          </div>
        )}
        alt=""
        onClose={() => setZoom(null)}
      />
    </>
  );
}
